<template>
  <div class="setting_firmware">
    <div class="device_info_card">
      <div class="device_icon_box">
        <img :src="getDeviceIcon()" class="device_icon" />
      </div>
      <div class="device_details">
        <div class="device_name">{{ $t(getDeviceName()) }}</div>
        <div class="version_badge">
          <span class="version_label">{{ $t('firmware_version') }}</span>
          <span class="version_value">{{ runMode }} {{ firmwareVersion == null ? $t('getting') : firmwareVersion }}</span>
        </div>
      </div>
    </div>

    <div class="setting_firmware_content">
      <div class="upgrade_section">
        <!-- 在线升级区域 -->
        <div class="upgrade_card online" :class="{ can_upgrade: firmware.isUpgrade.value }">
          <div class="card_info">
            <div class="card_title">{{ $t('upgrade_online') }}</div>
            <div class="card_desc">{{ firmware.isUpgrade.value ? $t('new_version_available') : $t('firmware_is_newest') }}</div>
          </div>
          <div v-if="firmware.isUpgrade.value" class="action_btn_container">
            <div class="upgrade_button primary_button" @click="upgradeOnline">
              <div class="icon_upgrade"></div>
              {{ $t('upgrade') }}
            </div>
          </div>
          <div v-else class="status_hint">
            <div class="icon_check"></div>
            {{ $t('firmware_is_newest') }}
          </div>
        </div>

        <!-- 本地升级区域 -->
        <div class="upgrade_card local" v-if="specialCheckCurrent(speciaBehiver.SupportLocalUpdate)">
          <div class="card_info">
            <div class="card_title">{{ $t('upgrade_local') }}</div>
            <div class="card_desc">{{ $t('select_firmware_file_to_update') }}</div>
          </div>
          <div class="action_btn_container">
            <div class="upgrade_button secondary_button" @click="upgradeLocal">
              {{ $t('open_file') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentDevice } from '@/profiles/selector'
import { openExternalLink } from '@/tool/utility'
import firmware from '@/upgrade/firmware'
import { isShowUpgradeFirmware } from '../firmware_upgrade/firmware_upgrade.vue'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { setOrRead } from '@/service/driver/set_read'
import { SpecificItem } from '@libs/types/enum'
import { onMounted, ref } from 'vue'
defineProps<{ firmwareVersion: string | null }>()
const getDeviceName = (): string => {
  if (getCurrentDevice.value != null) {
    return getCurrentDevice.value.name
  }
  return ''
}
const getDeviceIcon = (): string => {
  if (getCurrentDevice.value != null) {
    return getCurrentDevice.value.icon
  }
  return ''
}

const upgradeOnline = () => {
  if (firmware.isUpgrade.value) {
    openExternalLink(firmware.upgradeUrl)
  }
}
const upgradeLocal = () => {
  isShowUpgradeFirmware.value = true
}

const runMode = ref('')
onMounted(() => {
  getRunMode()
})
const getRunMode = async () => {
  if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDev) || specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix) || specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMagnet)) {
    setOrRead.read<string>(SpecificItem.RunMode).then((res: string) => {
      runMode.value = res
    })
  }
}
</script>

<style scoped lang="less">
.setting_firmware {
  width: 100%;
  height: 100%;
  padding-bottom: 20px;

  .device_info_card {
    display: flex;
    align-items: center;
    background: rgba(var(--color-brand-1-rgb), 0.03);
    border-radius: var(--radius-l);
    padding: 32px;
    margin-bottom: 24px;
    border: 1px solid rgba(var(--color-brand-1-rgb), 0.08);
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      right: -20px;
      top: -20px;
      width: 100px;
      height: 100px;
      background: var(--brand-color);
      opacity: 0.03;
      filter: blur(40px);
      border-radius: 50%;
    }

    .device_icon_box {
      width: 140px;
      height: 70px;
      margin-right: 28px;
      background: #fff;
      border-radius: var(--action-btn-radius);
      padding: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
      border: 1px solid rgba(255, 255, 255, 1);

      .device_icon {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }
    }

    .device_details {
      flex: 1;

      .device_name {
        font-size: 18px;
        font-weight: 800;
        color: var(--surface-text-primary);
        margin-bottom: 6px;
      }

      .version_badge {
        display: inline-flex;
        align-items: center;
        background: rgba(0, 0, 0, 0.05);
        border-radius: var(--action-btn-radius);
        padding: 4px 10px;
        font-size: 13px;

        .version_label {
          color: var(--surface-text-secondary);
          margin-right: 8px;
        }

        .version_value {
          color: var(--surface-text-primary);
          font-weight: 600;
          font-family: monospace;
        }
      }
    }
  }

  .setting_firmware_content {
    width: 100%;

    .upgrade_section {
      display: flex;
      flex-direction: column;
      gap: 16px;

      .upgrade_card {
        background: #fcfdfe;
        border: 1px solid rgba(0, 0, 0, 0.04);
        border-radius: var(--radius-l);
        padding: 24px 32px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        transition: var(--transition-base);

        &:hover {
          border-color: rgba(var(--color-brand-1-rgb), 0.15);
          background: #fff;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
        }

        &.online.can_upgrade {
          border-color: rgba(var(--color-brand-1-rgb), 0.2);
          background: rgba(var(--color-brand-1-rgb), 0.03);
          box-shadow: 0 12px 30px rgba(var(--color-brand-1-rgb), 0.05);
        }

        .card_info {
          flex: 1;

          .card_title {
            font-size: 15px;
            font-weight: 700;
            color: var(--surface-text-primary);
            margin-bottom: 4px;
          }

          .card_desc {
            font-size: 12px;
            color: var(--surface-text-secondary);
          }
        }

        .action_btn_container {
          margin-left: 20px;
        }

        .upgrade_button {
          height: 36px;
          padding: 0 20px;
          border-radius: var(--action-btn-radius);
          font-size: 13px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: var(--transition-base);

          &.primary_button {
            background: var(--brand-color);
            color: #fff;
            box-shadow: var(--action-btn-primary-shadow);

            &:hover {
              transform: translateY(-2px);
              box-shadow: var(--action-btn-primary-shadow-hover);
            }
          }

          &.secondary_button {
            background: #fff;
            border: 1px solid rgba(0, 0, 0, 0.1);
            color: var(--surface-text-primary);

            &:hover {
              background: #f8f9fa;
              border-color: rgba(0, 0, 0, 0.2);
              transform: translateY(-1px);
            }
          }
        }

        .status_hint {
          display: flex;
          align-items: center;
          color: #00bc7d; /* 翠绿成功色 */
          font-size: 13px;
          font-weight: 600;

          .icon_check {
            width: 16px;
            height: 16px;
            margin-right: 6px;
            background: currentColor;
            clip-path: polygon(14% 44%, 0 58%, 50% 100%, 100% 16%, 80% 0%, 43% 72%);
          }
        }
      }
    }
  }
}
</style>
